import { useCallback } from 'react';

import { useUpdateOrder as useUpdateOrderApi } from 'api/requests';
import { OrderEntity } from 'api/types/entity';
import { useServerErrorNotify } from 'hooks';
import { FormSubmit } from 'libs/form';

import { OrderFormType } from '../forms/orderForm';
import { getOrderSubmitData } from '../helpers';

import { useLockAndUnlockOrder } from './useLockAndUnlockOrder';

export const useUpdateOrder = (order: OrderEntity | null) => {
  const { lockOrderIfPossible, unlockOrder } = useLockAndUnlockOrder(order);

  const { updateOrder, error, loading: submitLoading } = useUpdateOrderApi();

  // Enable edit mode for the order
  const onEnableEdit = useCallback(
    async (onCallback: () => void) => {
      const isPossibleEdit = await lockOrderIfPossible?.();
      if (isPossibleEdit) {
        onCallback();
      }
    },
    [lockOrderIfPossible]
  );

  // Disable edit mode for the order
  const onDisableEdit = useCallback(
    async (onCallback: () => void) => {
      unlockOrder?.();
      onCallback();
    },
    [unlockOrder]
  );

  const onSubmit = useCallback<FormSubmit<OrderFormType>>(
    async (values, helpers) => {
      const data = getOrderSubmitData(values, order!);
      const updatedOrder = await updateOrder(
        order?.id!,
        order?.versionNumber!,
        data
      );

      if (updatedOrder) {
        helpers.setReadOnly(true);
      }
    },
    [order, updateOrder]
  );

  useServerErrorNotify(error);

  return {
    submitLoading,
    onEnableEdit,
    onDisableEdit,
    onSubmit,
  };
};
